import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useSitzung } from '../session/SessionProvider'
import {
  aktivierePush,
  holeGeraete,
  holeVapidSchluessel,
  istIos,
  istInstalliert,
  istPushUnterstuetzt,
  leseErlaubnis,
  meldeGeraetAb,
  type PushGeraet,
} from '../lib/push'
import { datumKurz, zeitKurz } from '../lib/datum'
import { Button, Card, ListItem } from '../ui'

export function Benachrichtigungen() {
  const { abfrage } = useSitzung()
  const me = abfrage.data
  const qc = useQueryClient()
  const [laeuft, setLaeuft] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  const [permission, setPermission] = useState<NotificationPermission | 'nicht-unterstuetzt'>(() =>
    leseErlaubnis(),
  )

  const vapid = useQuery({
    queryKey: ['push', 'vapid'],
    queryFn: () => holeVapidSchluessel(),
  })

  const geraete = useQuery({
    queryKey: ['push', 'geraete'],
    queryFn: () => holeGeraete(),
    enabled: Boolean(me && !me.setupRequired && !me.preview),
  })

  const hatKonto = Boolean(me && !me.setupRequired && !me.preview && me.user)
  const ios = istIos()
  const installiert = istInstalliert()
  const pushMoeglich = istPushUnterstuetzt()

  async function handleAktivieren() {
    setLaeuft(true)
    setFehler(null)
    const res = await aktivierePush()
    setPermission(leseErlaubnis())
    setLaeuft(false)
    if (!res.erfolg) {
      if (res.grund === 'verweigert') {
        setFehler('Benachrichtigungen wurden nicht erlaubt.')
      } else {
        setFehler(res.grund ?? 'Aktivierung fehlgeschlagen.')
      }
    } else {
      void qc.invalidateQueries({ queryKey: ['push', 'geraete'] })
    }
  }

  async function handleAbmelden(endpoint: string) {
    setLaeuft(true)
    try {
      await meldeGeraetAb(endpoint)
      void qc.invalidateQueries({ queryKey: ['push', 'geraete'] })
    } catch {
      setFehler('Abmelden fehlgeschlagen.')
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <>
      <div className="seitenkopf">
        <div>
          <h1>Benachrichtigungen</h1>
          <p>Web Push auf diesem und weiteren Geräten verwalten.</p>
        </div>
      </div>

      {fehler ? (
        <p className="fehler" role="alert">
          {fehler}
        </p>
      ) : null}

      {/* Fall 1: Ohne Konto (Erklärung statt Schalter) */}
      {!hatKonto ? (
        <Card titel="Web Push nicht verfügbar">
          <p className="unterzeile">
            Dieses Profil hat kein eigenes Benutzerkonto. Web-Push-Benachrichtigungen können nur
            für Personen mit eigenem Zugang eingerichtet werden.
          </p>
        </Card>
      ) : !pushMoeglich ? (
        <Card titel="Nicht unterstützt">
          <p className="unterzeile">
            Dein aktueller Browser unterstützt keine Web-Push-Benachrichtigungen.
          </p>
        </Card>
      ) : vapid.data === null ? (
        /* Fall 2: Push auf Server nicht eingerichtet */
        <Card titel="Web Push deaktiviert">
          <p className="unterzeile">
            Web-Push-Benachrichtigungen sind auf diesem Famora-Server zurzeit nicht eingerichtet.
          </p>
        </Card>
      ) : ios && !installiert ? (
        /* Fall 3: iOS ohne PWA-Installation (Anleitung Teilen -> Zum Home-Bildschirm) */
        <Card titel="Installation erforderlich">
          <p className="unterzeile">
            Auf dem iPhone und iPad empfängt Famora Benachrichtigungen nur als installierte Web-App.
          </p>
          <div className="stack-3">
            <p>
              1. Tippe in der Safari-Leiste auf das <strong>Teilen</strong>-Symbol.
            </p>
            <p>
              2. Wähle im Menü <strong>„Zum Home-Bildschirm“</strong>.
            </p>
            <p>
              3. Öffne Famora danach über das Symbol auf deinem Home-Bildschirm und kehre hierher
              zurück.
            </p>
          </div>
        </Card>
      ) : permission === 'denied' ? (
        /* Fall 4: Erlaubnis verweigert (Klartext statt Knopf) */
        <Card titel="Benachrichtigungen blockiert">
          <p className="unterzeile">
            Benachrichtigungen sind in deinem Browser für Famora blockiert. Nur deine
            Browsereinstellungen können sie wieder freischalten — ein Knopf hier kann das nicht.
          </p>
        </Card>
      ) : permission === 'default' ? (
        /* Fall 5: Erlaubnis offen (genau ein Knopf) */
        <Card titel="Benachrichtigungen aktivieren">
          <p className="unterzeile">
            Erhalte wichtige Erinnerungen an Aufgaben und Termine direkt auf deinen Sperrbildschirm.
          </p>
          <div className="reihe">
            <Button onClick={() => void handleAktivieren()} laeuft={laeuft}>
              Benachrichtigungen aktivieren
            </Button>
          </div>
        </Card>
      ) : (
        /* Fall 6: Eingerichtet */
        <Card titel="Status">
          <p className="unterzeile">
            Benachrichtigungen sind auf diesem Gerät aktiv. Du wirst bei neuen Aufgaben und
            Erinnerungen benachrichtigt.
          </p>
        </Card>
      )}

      {/* Liste der angemeldeten Geräte */}
      {hatKonto && (geraete.data ?? []).length > 0 ? (
        <Card titel="Angemeldete Geräte">
          <ul className="liste liste--gruppe">
            {(geraete.data ?? []).map((g: PushGeraet) => {
              const datumStr = g.lastUsedAt
                ? `Zuletzt benutzt am ${datumKurz(g.lastUsedAt)} um ${zeitKurz(g.lastUsedAt)}`
                : `Angemeldet am ${datumKurz(g.createdAt)}`
              return (
                <ListItem
                  key={g.id}
                  titel={g.userAgent ? g.userAgent.split(' ')[0] ?? 'Browser' : 'Unbekanntes Gerät'}
                  unterzeile={datumStr}
                  folgend={
                    <Button
                      variant="text"
                      disabled={laeuft}
                      onClick={() => void handleAbmelden(g.id)}
                    >
                      Abmelden
                    </Button>
                  }
                />
              )
            })}
          </ul>
        </Card>
      ) : null}
    </>
  )
}
